import { FC, useState } from "react";
import { SimpleText, SimpleDialog, SimpleInput } from "../../../components";
import { Box, IconButton } from "@mui/material";
import moment from "moment";

import {
  statusFunc,
  cancelAppointmentFunc,
  clearStatusFunc,
} from "../../../redux/features/appointment/appointmentSlice";
import { RootState, AppDispatch } from "../../../redux/store";
import { useSelector, useDispatch } from "react-redux";
import { SimpleButton } from "../../../components";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "../../../assets/images/svg";
type SmallDialogProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
};

const CheckStatusDialog: FC<SmallDialogProps> = ({ open, setOpen }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { status } = useSelector((state: RootState) => state?.appointment);
  const { contrast } = useSelector(
    (state: RootState) => state?.settings?.data?.disability,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [loadingCancel, setLoadingCancel] = useState<boolean>(false);

  const getTranslatedStatusOrganizationName = () => {
    if (currentLanguage === "hr") {
      return status?.organizationNameHr ?? status?.organizationName!;
    } else if (currentLanguage === "sr") {
      return status?.organizationNameSrb ?? status?.organizationName!;
    } else if (currentLanguage === "en") {
      return status?.organizationNameEng ?? status?.organizationName!;
    } else {
      return status?.organizationName ?? status?.organizationName!;
    }
  };

  const handleConfirm = () => {
    if (!status?.code) {
      const data = { code: code, email: email };
      dispatch(statusFunc({ setLoading, data, setValidation }));
    } else {
      setOpen(false);
      setTimeout(() => {
        setCode("");
        setEmail("");
        dispatch(clearStatusFunc());
      }, 500);
    }
  };
  const handleClose = () => {
    setOpen(false);

    setTimeout(() => {
      setCode("");
      setEmail("");
      dispatch(clearStatusFunc());
    }, 500);
  };

  const handleCancel = () => {
    dispatch(
      cancelAppointmentFunc({
        setLoading: setLoadingCancel,
        code: status?.code || "",
        email: status?.userEmail || "",
        setValidation,
      }),
    );
  };
  return (
    <SimpleDialog
      open={open}
      title="Checking reservation status"
      confirmLabel="Check"
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "#000",
          "&:focus": {
            outline: "none",
            boxShadow: "none",
          },
        }}
      >
        <CloseIcon color={contrast ? "#000" : "#001E96"} />
      </IconButton>
      {!status?.code ? (
        <Box sx={{ marginBottom: "55px" }}>
          <SimpleText
            value="Enter the code from your email to check the status of your reservation."
            type="dialogText"
            sx={{
              marginBottom: { md: "20px", lg: "30px" },
              marginLeft: "5px ",
              maxWidth: "450px",
            }}
          />
          <SimpleInput
            inputLabel="CODE"
            placeHolderLabel="XXXX"
            value={code}
            setParentState={setCode}
            type="string"
            style={{ width: "100%" }}
          />
          <SimpleInput
            inputLabel="YOUR E-MAIL"
            placeHolderLabel="mail@mail.com"
            value={email}
            setParentState={setEmail}
            type="string"
            style={{ width: "100%", marginTop: "10px" }}
          />
        </Box>
      ) : (
        <Box sx={{ marginBottom: "70px" }}>
          <Box
            sx={{ display: "flex", flexDirection: "row", marginBottom: "20px" }}
          >
            <SimpleText
              value={getTranslatedStatusOrganizationName()?.toUpperCase() || ""}
              type="grayBlack"
            />
          </Box>
          <Box
            sx={{ display: "flex", flexDirection: "row", marginBottom: "20px" }}
          >
            <SimpleText
              value="ADDRESS: "
              type="grayBlack"
              sx={{ whiteSpace: "nowrap" }}
            />
            <SimpleText
              value={status?.organizationAddress || ""}
              type="stepText"
              sx={{ marginLeft: "5px" }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <SimpleText
              value="TEL: "
              type="grayBlack"
              sx={{ whiteSpace: "nowrap" }}
            />
            <SimpleText
              value={status?.contactNumber || ""}
              type="stepText"
              sx={{ whiteSpace: "nowrap", marginLeft: "5px" }}
            />
          </Box>
          <Box
            sx={{ display: "flex", flexDirection: "row", marginBottom: "20px" }}
          >
            <SimpleText
              value="MAIL: "
              type="grayBlack"
              sx={{ whiteSpace: "nowrap" }}
            />
            <SimpleText
              value={status?.contactEmail || ""}
              type="stepText"
              sx={{ marginLeft: "5px" }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { sm: "row", lg: "row", xs: "column" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ marginRight: "45px" }}>
              <SimpleText value="CODE" type="labelText" />
              <SimpleText
                value={status?.code || ""}
                type="stepCode"
                sx={{ whiteSpace: "nowrap" }}
              />
              <SimpleText
                value="STATUS"
                type="labelText"
                sx={{ marginTop: "15px" }}
              />
              <SimpleText
                value={status?.status?.name || ""}
                type={
                  status?.status?.name == "Approved" ? "greenCode" : "redCode"
                }
                sx={{ whiteSpace: "nowrap" }}
              />
            </Box>
            <Box sx={{ marginRight: "50px" }}>
              <SimpleText
                value="CATEGORY: "
                type="grayBlack"
                sx={{ whiteSpace: "nowrap" }}
              />
              <SimpleText
                value={status?.categoryName || ""}
                type="stepText"
                sx={{}}
              />
              <SimpleText
                value="SERVICE: "
                type="grayBlack"
                sx={{ whiteSpace: "nowrap" }}
              />
              <SimpleText
                value={status?.serviceName || ""}
                type="stepText"
                sx={{}}
              />
              <SimpleText
                value="DATE AND TIME: "
                type="grayBlack"
                sx={{ whiteSpace: "nowrap" }}
              />
              <SimpleText
                value={`${moment(status?.date).format("DD.MM.YYYY")}, ${status?.startTime?.slice(0, 5)}h`}
                //value={`${status?.startTime?.slice(0, 5)}` + "h" || ""}
                type="stepText"
                sx={{}}
              />
              <SimpleText
                value="NUMBER OF PERSONS: "
                type="grayBlack"
                sx={{ whiteSpace: "nowrap" }}
              />
              <SimpleText
                value={status?.numberOfPeople?.toString() || "1"}
                type="stepText"
                sx={{}}
              />
              {status?.requiredDocuments &&
                status?.requiredDocuments?.length > 0 && (
                  <SimpleText
                    value="REQUIRED DOCUMENTS: "
                    type="grayBlack"
                    sx={{ whiteSpace: "nowrap" }}
                  />
                )}
              {status?.requiredDocuments?.map((item, index) => (
                <Box key={index}>
                  <SimpleText
                    value={`• ${item}`}
                    type="stepText"
                    sx={{
                      marginLeft: "5px ",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column-reverse",
            sm: "row",
            lg: "row",
          },

          marginLeft: "5px",
          zIndex: 999,
          background: "#fff",
          justifyContent: "end",
          marginBottom: "-35px",
        }}
      >
        {status?.status?.name == "Approved" && (
          <SimpleButton
            buttonLabel={t("Cancel")}
            onClick={handleCancel}
            sx={{
              minWidth: {
                xs: "70vw",
                sm: "150px",
                lg: "150px",
              },
              marginRight: {
                xs: "1vw",
                sm: "15px",
                lg: "15px",
              },
            }}
            type={contrast ? "subButton" : "redButton"}
            loading={loadingCancel}
          />
        )}

        {handleConfirm && (
          <SimpleButton
            buttonLabel={!status?.code ? t("Check") : t("Close")}
            onClick={handleConfirm}
            sx={{
              minWidth: {
                xs: "70vw",
                sm: "150px",
                lg: "150px",
              },
              marginRight: {
                xs: "1vw",
                sm: "15px",
                lg: "15px",
              },
              marginBottom: {
                xs: "25px",
                sm: "0px",
                lg: "0px",
              },
            }}
            type="mainButton"
            loading={loading}
            disabled={!email || !code}
          />
        )}
      </Box>
    </SimpleDialog>
  );
};

export default CheckStatusDialog;
